import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { Search, Plus, Upload, Trash2, History, Pencil, Download } from "lucide-react";

const FAMILIA_LABELS = {
  material: "Materiales",
  mano_obra: "Mano de Obra",
  herramienta: "Herramienta",
  equipo: "Equipo",
  seguridad_minera: "Seguridad Minera",
  explosivos: "Explosivos",
  acero_barrenacion: "Acero de Barrenación",
  basico: "Básicos / Auxiliares",
};

const empty = { codigo: "", descripcion: "", unidad: "", familia: "material", precio: 0, moneda: "MXN" };

export default function InsumosView() {
  const { user } = useAuth();
  const canWrite = user?.role !== "consulta";
  const [familias, setFamilias] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [familia, setFamilia] = useState("todos");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // {mode, data}
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editPrice, setEditPrice] = useState({}); // id -> value while editing
  const [famDialog, setFamDialog] = useState(null); // {mode, key, label}

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (familia !== "todos") params.familia = familia;
      const { data } = await api.get("/insumos", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadFamilias = async () => { const { data } = await api.get("/familias"); setFamilias(data); };
  useEffect(() => {
    loadFamilias();
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, familia]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => (c[i.familia] = (c[i.familia] || 0) + 1));
    return c;
  }, [items]);

  const savePrice = async (item, value) => {
    const precio = parseFloat(value);
    if (isNaN(precio) || precio === item.precio) {
      setEditPrice((e) => ({ ...e, [item.id]: undefined }));
      return;
    }
    try {
      await api.put(`/insumos/${item.id}`, { ...item, precio });
      toast.success(`Precio actualizado: ${item.codigo}`);
      setEditPrice((e) => ({ ...e, [item.id]: undefined }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitDialog = async () => {
    const d = dialog.data;
    try {
      if (dialog.mode === "edit") {
        await api.put(`/insumos/${d.id}`, d);
        toast.success("Insumo actualizado");
      } else {
        await api.post("/insumos", d);
        toast.success("Insumo creado");
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.codigo}?`)) return;
    try {
      await api.delete(`/insumos/${item.id}`);
      toast.success("Insumo eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadTemplate = () => {
    const csv =
      "codigo,descripcion,unidad,familia,precio\nMAT-100,Ejemplo material,m3,material,123.45\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_insumos.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveFamilia = async () => {
    const label = (famDialog?.label || "").trim();
    if (!label) { toast.error("Escribe un nombre"); return; }
    try {
      if (famDialog.mode === "edit") await api.put(`/familias/${famDialog.key}`, { label });
      else await api.post("/familias", { label });
      toast.success("Familia guardada");
      setFamDialog(null); loadFamilias();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeFamilia = async (f) => {
    if (!window.confirm(`¿Eliminar la familia "${f.label}"?`)) return;
    try {
      await api.delete(`/familias/${f.key}`);
      toast.success("Familia eliminada");
      if (familia === f.key) setFamilia("todos");
      loadFamilias();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-1 min-h-0">
      {/* Middle panel - familias */}
      <div className="w-52 shrink-0 border-r border-border flex flex-col">
        <div className="px-3 h-10 flex items-center justify-between border-b border-border">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Familias
          </span>
          {canWrite && (
            <button onClick={() => setFamDialog({ mode: "create", label: "" })} className="text-muted-foreground hover:text-primary" title="Nueva familia" data-testid="new-familia-btn">
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1 py-1">
          <FamiliaRow label="Todos los insumos" active={familia === "todos"} onClick={() => setFamilia("todos")} count={items.length} testid="fam-todos" />
          {familias.map((f) => (
            <FamiliaRow
              key={f.key}
              label={f.label}
              active={familia === f.key}
              onClick={() => setFamilia(f.key)}
              count={counts[f.key]}
              testid={`fam-${f.key}`}
              custom={f.custom}
              canWrite={canWrite}
              onEdit={() => setFamDialog({ mode: "edit", key: f.key, label: f.label })}
              onDelete={() => removeFamilia(f)}
            />
          ))}
        </div>
      </div>

      {/* Right - grid */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="h-12 shrink-0 border-b border-border flex items-center gap-2 px-3">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="insumo-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por código o descripción…"
              className="pl-8 h-8 rounded-sm"
            />
          </div>
          <span className="font-mono-num text-xs text-muted-foreground">
            {items.length} insumos
          </span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Plantilla</span>
          </Button>
          {canWrite && (
            <>
              <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1" onClick={() => setUploadOpen(true)} data-testid="bulk-upload-btn">
                <Upload className="h-4 w-4" /> <span className="hidden sm:inline">Carga masiva</span>
              </Button>
              <Button size="sm" className="h-8 rounded-sm gap-1" onClick={() => setDialog({ mode: "create", data: { ...empty, familia: familia !== "todos" ? familia : "material" } })} data-testid="new-insumo-btn">
                <Plus className="h-4 w-4" /> Nuevo
              </Button>
            </>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="qc-table">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Código</th>
                <th>Descripción</th>
                <th style={{ width: 70 }}>Unidad</th>
                <th style={{ width: 150 }}>Familia</th>
                <th style={{ width: 110 }} className="text-right">Precio</th>
                <th style={{ width: 70 }} className="text-center">Moneda</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`insumo-row-${it.codigo}`}>
                  <td className="font-mono-num text-primary">{it.codigo}</td>
                  <td>{it.descripcion}</td>
                  <td className="font-mono-num text-muted-foreground">{it.unidad}</td>
                  <td className="text-xs text-muted-foreground">{FAMILIA_LABELS[it.familia] || it.familia}</td>
                  <td className="text-right">
                    {editPrice[it.id] !== undefined ? (
                      <input
                        autoFocus
                        type="number"
                        step="0.000001"
                        defaultValue={it.precio}
                        onBlur={(e) => savePrice(it, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                          if (e.key === "Escape") setEditPrice((s) => ({ ...s, [it.id]: undefined }));
                        }}
                        className="w-full text-right font-mono-num bg-background border border-primary rounded-sm px-1 py-0.5 outline-none"
                        data-testid={`price-input-${it.codigo}`}
                      />
                    ) : (
                      <button
                        disabled={!canWrite}
                        onClick={() => canWrite && setEditPrice((s) => ({ ...s, [it.id]: it.precio }))}
                        className="font-mono-num hover:text-primary w-full text-right disabled:cursor-default"
                        data-testid={`price-cell-${it.codigo}`}
                      >
                        {fmt(it.precio, 6)}
                      </button>
                    )}
                  </td>
                  <td className="text-center font-mono-num text-xs text-muted-foreground" data-testid={`insumo-moneda-${it.codigo}`}>{it.moneda || "MXN"}</td>
                  <td>
                    <div className="flex items-center justify-end gap-0.5">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" title="Historial de precios" data-testid={`history-btn-${it.codigo}`}>
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="end">
                          <HistoryList insumoId={it.id} />
                        </PopoverContent>
                      </Popover>
                      {canWrite && (
                        <>
                          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={() => setDialog({ mode: "edit", data: { ...it } })} data-testid={`edit-btn-${it.codigo}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm text-destructive" onClick={() => remove(it)} data-testid={`delete-btn-${it.codigo}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted-foreground py-10">
                    No hay insumos. Crea uno o usa la carga masiva.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head">
              {dialog?.mode === "edit" ? "Editar insumo" : "Nuevo insumo"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código</Label>
                <Input className="mt-1 font-mono-num" value={dialog.data.codigo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, codigo: e.target.value } }))} data-testid="insumo-form-codigo" />
              </div>
              <div>
                <Label className="text-xs">Unidad</Label>
                <Input className="mt-1 font-mono-num" value={dialog.data.unidad} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, unidad: e.target.value } }))} data-testid="insumo-form-unidad" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Descripción</Label>
                <Input className="mt-1" value={dialog.data.descripcion} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, descripcion: e.target.value } }))} data-testid="insumo-form-descripcion" />
              </div>
              <div>
                <Label className="text-xs">Familia</Label>
                <Select value={dialog.data.familia} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, familia: v } }))}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="insumo-form-familia"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {familias.map((f) => (
                      <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Precio</Label>
                <div className="flex gap-1 mt-1">
                  <Input type="number" step="0.000001" className="font-mono-num" value={dialog.data.precio} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, precio: parseFloat(e.target.value) || 0 } }))} data-testid="insumo-form-precio" />
                  <Select value={dialog.data.moneda || "MXN"} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, moneda: v } }))}>
                    <SelectTrigger className="w-24 rounded-sm" data-testid="insumo-form-moneda"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">MXN</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={submitDialog} data-testid="insumo-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onDone={() => { load(); loadFamilias(); }} />

      <Dialog open={!!famDialog} onOpenChange={(o) => !o && setFamDialog(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-head">{famDialog?.mode === "edit" ? "Editar familia" : "Nueva familia"}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs">Nombre de la familia</Label>
            <Input autoFocus className="mt-1" value={famDialog?.label || ""} onChange={(e) => setFamDialog((d) => ({ ...d, label: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && saveFamilia()} data-testid="familia-form-label" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setFamDialog(null)}>Cancelar</Button>
            <Button className="rounded-sm" onClick={saveFamilia} data-testid="familia-form-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FamiliaRow({ label, active, onClick, count, testid, custom, canWrite, onEdit, onDelete }) {
  return (
    <div
      className={`group w-full flex items-center justify-between px-3 h-8 text-sm transition-colors ${
        active ? "bg-accent/60 text-foreground border-l-2 border-primary" : "text-muted-foreground hover:bg-accent/40 border-l-2 border-transparent"
      }`}
    >
      <button onClick={onClick} data-testid={testid} className="flex-1 min-w-0 text-left truncate">{label}</button>
      <div className="flex items-center gap-1 shrink-0">
        {count != null && <span className="font-mono-num text-[10px] text-muted-foreground">{count}</span>}
        {canWrite && custom && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary" title="Editar" data-testid={`edit-${testid}`}><Pencil className="h-3 w-3" /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-destructive" title="Eliminar" data-testid={`delete-${testid}`}><Trash2 className="h-3 w-3" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function HistoryList({ insumoId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/insumos/${insumoId}/historial`).then(({ data }) => setRows(data));
  }, [insumoId]);
  return (
    <div className="max-h-64 overflow-auto">
      <div className="px-3 py-2 border-b border-border text-xs font-medium">Historial de precios</div>
      {rows === null && <div className="p-3 text-xs text-muted-foreground">Cargando…</div>}
      {rows && rows.length === 0 && <div className="p-3 text-xs text-muted-foreground">Sin cambios registrados.</div>}
      {rows && rows.map((r) => (
        <div key={r.id} className="px-3 py-2 border-b border-border/60 text-xs flex items-center justify-between gap-2">
          <div className="font-mono-num">
            <span className="text-muted-foreground line-through">{fmt(r.precio_anterior, 2)}</span>
            {" → "}
            <span className="text-primary">{fmt(r.precio_nuevo, 2)}</span>
          </div>
          <div className="text-[10px] text-muted-foreground text-right">
            {new Date(r.fecha).toLocaleDateString("es-MX")}
          </div>
        </div>
      ))}
    </div>
  );
}

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/insumos/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Carga completada: ${data.insertados} nuevos, ${data.actualizados} actualizados`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head">Carga masiva de insumos</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Sube un archivo <span className="font-mono-num">.xlsx</span> o{" "}
          <span className="font-mono-num">.csv</span> con columnas: código, descripción, unidad,
          familia, precio. Los códigos existentes se actualizan (con historial de precio).
        </p>
        <div
          className="border border-dashed border-border rounded-sm p-8 text-center cursor-pointer hover:bg-accent/40 transition-colors"
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
        >
          <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
          <span className="text-sm">{busy ? "Procesando…" : "Haz clic para seleccionar archivo"}</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
          />
        </div>
        {result && (
          <div className="text-sm font-mono-num border border-border rounded-sm p-3 space-y-1">
            <div>Insertados: <span className="text-primary">{result.insertados}</span></div>
            <div>Actualizados: <span className="text-primary">{result.actualizados}</span></div>
            <div>Errores: <span className="text-destructive">{result.errores?.length || 0}</span></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
